import Autocomplete from '@material-ui/lab/Autocomplete';
import FormControl from '@material-ui/core/FormControl';
import MenuItem from '@material-ui/core/MenuItem';
import React, { useState } from 'react';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import ToggleSwitch from '../../../../components/ToggleSwitch';
import useSaveSegmentRemodelConfig from '../../../../graphql/hooks/useSaveSegmentRemodelConfig';
import { Button } from '@attain-sre/attain-design-system';
import {
  OTS_SGM_MNG_REMODELING_LAST_REMODELED,
  OTS_SGM_MNG_REMODELING_NEXT_REMODEL,
  OTS_SGM_MNG_REMODELING_OPTIMIZATION_OPTIONS,
  OTS_SGM_MNG_REMODELING_OPTIMIZED_AUDIENCE_LABEL,
  OTS_SGM_MNG_REMODELING_OPTIMIZED_AUDIENCE_PLACEHOLDER,
  OTS_SGM_MNG_REMODELING_SCHEDULE_ERROR,
  OTS_SGM_MNG_REMODELING_SCHEDULE_ERROR_GENERIC,
  OTS_SGM_MNG_REMODELING_SCHEDULE_FREQUENCY_LABEL,
  OTS_SGM_MNG_REMODELING_SCHEDULE_MODAL_TITLE,
  OTS_SGM_MNG_REMODELING_SCHEDULE_OFF,
  OTS_SGM_MNG_REMODELING_SCHEDULE_ON,
  OTS_SGM_MNG_REMODELING_SCHEDULE_OPTIONS,
  OTS_SGM_MNG_REMODELING_SCHEDULE_SAVE,
  OTS_SGM_MNG_REMODELING_SCHEDULE_SUCCESS,
  OTS_SGM_MNG_REMODELING_SCHEDULE_TOGGLE_DESCRIPTION,
  OTS_SGM_MNG_REMODELING_SCHEDULE_TOGGLE_LABEL,
} from '../../../../content';
import { OptimizedAudience, RemodelingScheduleConfig } from '../../types';
import { SCHEDULE_FREQUENCY_OPTIONS } from '../../constants';
import { X } from '@phosphor-icons/react';
import { addNotification } from '../../slice';
import { useAppDispatch } from '../../../../redux/hooks';
import * as Styled from './index.styles';

interface UpdateScheduleModalProps {
  segmentId: string;
  config: RemodelingScheduleConfig;
  lastExportedDate: string;
  nextExportDate: string;
  onClose: () => void;
  onSave: (config: RemodelingScheduleConfig) => void;
}

const UpdateScheduleModal: React.FC<UpdateScheduleModalProps> = ({
  segmentId,
  config,
  lastExportedDate,
  nextExportDate,
  onClose,
  onSave,
}) => {
  const dispatch = useAppDispatch();
  const [localConfig, setLocalConfig] = useState<RemodelingScheduleConfig>({
    ...config,
  });

  const [saveConfig, { loading: saving }] = useSaveSegmentRemodelConfig();

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalConfig({
      ...localConfig,
      enabled: event.target.checked,
    });
  };

  const handleFrequencyChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setLocalConfig({
      ...localConfig,
      frequency: Number(event.target.value) || localConfig.frequency,
    });
  };

  const handleAudienceSelect = (
    _: React.ChangeEvent<{}>,
    newValue: OptimizedAudience | null
  ) => {
    setLocalConfig({
      ...localConfig,
      optimizedAudienceId: newValue?.optimized_audience_id ?? null,
    });
  };

  const handleSave = async () => {
    try {
      const result = await saveConfig({
        segmentId,
        remodelInterval: localConfig.frequency,
        enabled: localConfig.enabled,
        optimizedAudienceId: localConfig.optimizedAudienceId ?? null,
      });

      const responseData = result?.data?.saveTtdSegmentRemodelConfiguration;

      if (responseData?.success) {
        dispatch(
          addNotification({
            state: 'done',
            message: OTS_SGM_MNG_REMODELING_SCHEDULE_SUCCESS,
          })
        );
        onSave(localConfig);
        onClose();
      } else {
        const errorMessage =
          responseData?.message || OTS_SGM_MNG_REMODELING_SCHEDULE_ERROR;
        dispatch(
          addNotification({
            state: 'error',
            message: errorMessage,
          })
        );
      }
    } catch (error: any) {
      const errorMessage =
        error?.message || OTS_SGM_MNG_REMODELING_SCHEDULE_ERROR_GENERIC;
      dispatch(
        addNotification({
          state: 'error',
          message: errorMessage,
        })
      );
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Styled.Backdrop onClick={handleBackdropClick}>
      <Styled.ModalContainer>
        <Styled.Header>
          <Styled.Title>
            {OTS_SGM_MNG_REMODELING_SCHEDULE_MODAL_TITLE}
          </Styled.Title>
          <Styled.CloseButton onClick={onClose}>
            <X size={24} />
          </Styled.CloseButton>
        </Styled.Header>

        <Styled.Content>
          <Styled.LeftPanel>
            <Styled.ToggleSection>
              <Styled.ToggleLabel>
                {OTS_SGM_MNG_REMODELING_SCHEDULE_TOGGLE_LABEL}
              </Styled.ToggleLabel>
              <Styled.ToggleRow>
                <ToggleSwitch
                  checked={localConfig.enabled}
                  onChange={handleToggleChange}
                  disableRipple
                />
                <Styled.ToggleContent>
                  <Styled.ToggleStatus>
                    {localConfig.enabled
                      ? OTS_SGM_MNG_REMODELING_SCHEDULE_ON
                      : OTS_SGM_MNG_REMODELING_SCHEDULE_OFF}
                  </Styled.ToggleStatus>
                  <Styled.ToggleDescription>
                    {OTS_SGM_MNG_REMODELING_SCHEDULE_TOGGLE_DESCRIPTION}
                  </Styled.ToggleDescription>
                </Styled.ToggleContent>
              </Styled.ToggleRow>
            </Styled.ToggleSection>

            {/* Schedule options */}
            <div>
              <Styled.SectionHeader>
                {OTS_SGM_MNG_REMODELING_SCHEDULE_OPTIONS}
              </Styled.SectionHeader>
              <Styled.ScheduleOptions>
                <Styled.FrequencyRow>
                  <Styled.FrequencyLabel>
                    {OTS_SGM_MNG_REMODELING_SCHEDULE_FREQUENCY_LABEL}
                  </Styled.FrequencyLabel>
                  <Styled.DropdownWrapper>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Select
                        value={localConfig.frequency}
                        onChange={handleFrequencyChange}
                      >
                        {SCHEDULE_FREQUENCY_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Styled.DropdownWrapper>
                </Styled.FrequencyRow>
              </Styled.ScheduleOptions>
            </div>

            <Styled.SectionDivider />

            {/* Optimization options */}
            <div>
              <Styled.SectionHeader>
                {OTS_SGM_MNG_REMODELING_OPTIMIZATION_OPTIONS}
              </Styled.SectionHeader>
              <Styled.ScheduleOptions>
                <Styled.FrequencyRow>
                  <Styled.FrequencyLabel>
                    {OTS_SGM_MNG_REMODELING_OPTIMIZED_AUDIENCE_LABEL}:
                  </Styled.FrequencyLabel>
                  <Styled.DropdownWrapper>
                    <Autocomplete<OptimizedAudience>
                      options={localConfig.optimizedAudiences ?? []}
                      getOptionLabel={(option) =>
                        option.name ?? option.optimized_audience_id
                      }
                      value={
                        (localConfig.optimizedAudiences ?? []).find(
                          (a) =>
                            a.optimized_audience_id ===
                            localConfig.optimizedAudienceId
                        ) ?? null
                      }
                      onChange={handleAudienceSelect}
                      clearOnEscape
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          placeholder={OTS_SGM_MNG_REMODELING_OPTIMIZED_AUDIENCE_PLACEHOLDER}
                        />
                      )}
                    />
                  </Styled.DropdownWrapper>
                </Styled.FrequencyRow>
              </Styled.ScheduleOptions>
            </div>
          </Styled.LeftPanel>

          <Styled.RightPanel>
            <Styled.InfoItem>
              <Styled.InfoLabel>
                {OTS_SGM_MNG_REMODELING_LAST_REMODELED}
              </Styled.InfoLabel>
              <Styled.InfoValue>{lastExportedDate}</Styled.InfoValue>
            </Styled.InfoItem>
            <Styled.InfoItem>
              <Styled.InfoLabel>
                {OTS_SGM_MNG_REMODELING_NEXT_REMODEL}
              </Styled.InfoLabel>
              <Styled.InfoValue>{nextExportDate}</Styled.InfoValue>
            </Styled.InfoItem>
          </Styled.RightPanel>
        </Styled.Content>

        <Styled.Footer>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : OTS_SGM_MNG_REMODELING_SCHEDULE_SAVE}
          </Button>
        </Styled.Footer>
      </Styled.ModalContainer>
    </Styled.Backdrop>
  );
};

export default UpdateScheduleModal;
